import CustomSteps from "@funds/components/CustomSteps";
import CustomBtn from "@funds/pages/MyFunds/pages/StakingPage/parts/shared/CustomBtn/CustomBtn";
import PercentageBtns from "@funds/pages/MyFunds/pages/StakingPage/parts/shared/PercentageBtns";
import Section from "@funds/pages/MyFunds/pages/StakingPage/parts/shared/Section/Section";
import {
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  notification,
} from "antd";
import React, { useMemo, useState } from "react";
import Big from "big.js";
import * as Yup from "yup";
import { useFormik } from "formik";
import { connect } from "react-redux";
import { TReducers } from "@features/reducers";
import { IPESWalletDetails } from "@market/interfaces/IWallet";
import {
  setAccessingFundsAgreementCreatedAt,
  setFundsData,
} from "@funds/features/action.funds";
import { RouteComponentProps, withRouter } from "react-router-dom";
import queryString from "query-string";
import Faq from "@funds/pages/MyFunds/pages/StakingPage/parts/shared/Faq";
import requestToSellFaq from "./requestToSellFaq";
import ReactHtmlParser from "react-html-parser";
import { validDecimalNumber } from "@bundles/regexps";
import SummaryTable, {
  SummaryTableRow,
} from "@appComponents/SummaryTable/SummaryTable";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import PublicSharesService from "@services/PublicShares.service";
import { error } from "@appComponents/InfoBox/InfoBoxStyle.module.scss";

import * as style from "./RequestToSellStyle.module.scss";

const valueCannotBeEstimatedMsg = "Value cannot be estimated at this time";

type Props = {
  userPublicShares: IPESWalletDetails[];
  investorId: number;
  companyInvestorId: number | undefined;
  setFundsData: (investorId: number, refetch?: boolean) => Promise<undefined>;
} & RouteComponentProps<{ slug: string }>;

type SummaryData = {
  amount_of_shares_to_be_sold: number;
  estimated_net_sale: number;
  estimated_share_prices: number;
  estimated_share_proceed: number;
  sale_fee: number;
  share_fee: number;
  share_fee_usd: number;
  rounding_message?: string;
  isError?: boolean;
};

const RequestToSell = ({
  userPublicShares = [],
  investorId,
  companyInvestorId,
  setFundsData,
  location,
  history,
  match,
}: Props) => {
  const [step, setStep] = useState(0);
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedShareToSell, setSelectedShareToSell] = useState(
    queryString.parse(location.search).share
  );

  const [summaryData, setSummaryData] = useState<SummaryData | {}>({});
  const [errorMsg, setErrorMsg] = useState("");

  const [isLoading, setIsLoading] = useState(true);

  React.useEffect(() => {
    const id = companyInvestorId || investorId;

    if (!id) throw new Error("ID is missing");

    setFundsData(id)
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        notification.error({ message: "Something went wrong" });
      });
  }, []);

  const publicShareOptions = useMemo(
    () =>
      userPublicShares
        .filter((share) => Big(share.balance).gt(0))
        .map((share) => ({
          label: share.currency_name,
          value: share.currency,
        })),
    [userPublicShares]
  );

  const formik = useFormik({
    initialValues: {
      amount: "",
      name: selectedShareToSell,
      agreement1: false,
      agreement2: false,
      agreement3: false,
    },
    validationSchema: Yup.object().shape({
      agreement1: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
      agreement2: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
      agreement3: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
      amount: Yup.string()
        .trim()
        .required("Amount is required")
        .test(
          "validate-min-amount",
          `Please enter an amount greater than or equal to 0.001 ${selectedShareToSell?.toLocaleUpperCase()}`,
          (value) => {
            if (!value) return true;
            try {
              return !Big(value).lt(0.001);
            } catch {
              return false;
            }
          }
        )
        .test(
          "validate-amount",
          `Provided value is higher than the amount of your ${selectedShareToSell?.toLocaleUpperCase()} public shares`,
          (value, form) => {
            const shareDetails = userPublicShares.find(
              (share) => share.currency === form.parent.name
            );

            if (!value) return true;
            return (
              !Big(value).gt(shareDetails?.balance ?? 0) && !Big(value).eq(0)
            );
          }
        ),
    }),

    onSubmit: async (values) => {
      try {
        await PublicSharesService.confirmRequest({
          currency: values.name,
          amount: values.amount,
          investor_id: companyInvestorId || investorId,
        });

        await setFundsData(companyInvestorId || investorId, true);

        const sharesLink = `/profile/${match.params.slug}/shares/public-equity`;

        history.push(sharesLink);
        notification.success({
          message:
            "Confirmation: You've successfully submitted your Sell Share request.",
        });
      } catch (err) {
        if (err?.response?.data?.error) {
          notification.error({
            message: err.response.data.error,
          });
        }
      }
    },
    validateOnMount: false,
  });

  const getEstimations = async () => {
    const params = new URLSearchParams({
      currency: formik.values.name,
      amount: formik.values.amount,
    });

    try {
      const estimations = await PublicSharesService.getSharesEstimations(
        params.toString()
      );
      setSummaryData(estimations.data);
      setErrorMsg("");
      setStep(1);
    } catch (error) {
      setSummaryData({});

      if (error.response.status === 422) {
        setStep(1);
        setSummaryData({ ...error?.response?.data, isError: true });

        if (error?.response?.data?.error) {
          setErrorMsg(error.response.data.error);
        }
        return;
      } else {
        notification.error({ message: "Something went wrong" });
      }
    }
  };

  const shareDetails = useMemo(
    () =>
      userPublicShares.find((share) => share.currency === formik.values.name),
    [formik.values.name, userPublicShares]
  );

  const isSummaryDataExist = useMemo(
    () => !!Object.keys(summaryData).length,
    [summaryData]
  );

  const handleAmountPercentage = async (amount: number) => {
    if (!shareDetails) throw new Error("Wallet not selected");
    const { balance } = shareDetails;
    const result = Big(balance).times(Big(amount).div(100)).round(8).toString();
    await formik.setFieldValue("amount", result, true);
    setSelectedTag(amount.toString());
  };

  const handleAmountInputChange = (value: any) => {
    if (!shareDetails) throw new Error("Wallet not selected");
    if (!validDecimalNumber.test(value.target.value))
      throw new Error("Invalid amount");
    const { balance } = shareDetails;

    setSelectedTag(
      Big(value.target.value).div(Big(balance)).times(100).toString()
    );
  };

  if (isLoading) {
    return (
      <Space
        align="center"
        style={{ justifyContent: "center", width: "100%", minHeight: 300 }}
      >
        <Spin size="large" />
      </Space>
    );
  }

  const renderSummary = () => {
    return (
      <>
        <SummaryTable>
          <SummaryTableRow
            label={`Amount requested`}
            value={
              <CurrencyElement
                value={formik.values.amount}
                currency={shareDetails?.currency ?? ""}
              />
            }
          />
          {!!(summaryData as SummaryData).rounding_message && (
            <SummaryTableRow
              label={""}
              value={(summaryData as SummaryData).rounding_message ?? ""}
            />
          )}
          <SummaryTableRow
            label={`Estimated share price`}
            value={
              (summaryData as SummaryData)?.estimated_share_prices ? (
                <CurrencyElement
                  value={(summaryData as SummaryData)?.estimated_share_prices}
                  currency={"usd"}
                />
              ) : (
                valueCannotBeEstimatedMsg
              )
            }
          />

          {!!isSummaryDataExist && (
            <>
              <SummaryTableRow
                label={`Shares Name`}
                value={shareDetails?.currency_name ?? ""}
              />

              <SummaryTableRow
                label={`Amount to be sold`}
                value={
                  <CurrencyElement
                    value={
                      (summaryData as SummaryData)
                        ?.amount_of_shares_to_be_sold ?? formik.values.amount
                    }
                    currency={shareDetails?.currency ?? ""}
                  />
                }
              />

              <SummaryTableRow
                label={`Estimated share price`}
                value={
                  (summaryData as SummaryData)?.estimated_share_prices ? (
                    <CurrencyElement
                      value={
                        (summaryData as SummaryData)?.estimated_share_prices
                      }
                      currency={"usd"}
                    />
                  ) : (
                    valueCannotBeEstimatedMsg
                  )
                }
              />

              <SummaryTableRow
                label={`Estimated sale proceeds`}
                value={
                  (summaryData as SummaryData)?.estimated_share_proceed ? (
                    <CurrencyElement
                      value={
                        (summaryData as SummaryData)?.estimated_share_proceed
                      }
                      currency={"usd"}
                    />
                  ) : (
                    valueCannotBeEstimatedMsg
                  )
                }
              />

              <SummaryTableRow
                label={`Sale fee`}
                value={
                  <CurrencyElement
                    value={(summaryData as SummaryData)?.sale_fee ?? 0}
                    currency={"usd"}
                  />
                }
              />

              <SummaryTableRow
                label={`Est. net sale proceeds`}
                value={
                  (summaryData as SummaryData)?.estimated_net_sale ? (
                    <CurrencyElement
                      value={(summaryData as SummaryData)?.estimated_net_sale}
                      currency={"usd"}
                    />
                  ) : (
                    valueCannotBeEstimatedMsg
                  )
                }
              />

              <SummaryTableRow
                label={"Share fee"}
                value={
                  <CurrencyElement
                    value={(summaryData as SummaryData)?.share_fee ?? 0}
                    currency={shareDetails?.currency ?? ""}
                  />
                }
              />
            </>
          )}
        </SummaryTable>

        {!!errorMsg && <p className={style.errorMessage}>{errorMsg}</p>}

        {isSummaryDataExist && !(summaryData as SummaryData).isError && (
          <>
            <Form.Item
              required
              hasFeedback
              help={
                formik.touched.agreement1 ? formik.errors.agreement1 : false
              }
              className={style.label}
            >
              <div className={style.checkboxWraper}>
                <Checkbox
                  {...formik.getFieldProps("agreement1")}
                  checked={formik.values.agreement1}
                  id="agreement1"
                >
                  <Typography.Text>
                    <label htmlFor="agreement1" className={style.label}>
                      I understand that a third party provides an estimate for
                      the sale proceeds in USD and the estimation is only
                      updated once every day.
                    </label>
                  </Typography.Text>
                </Checkbox>
              </div>
            </Form.Item>
            <Form.Item
              required
              hasFeedback
              help={
                formik.touched.agreement2 ? formik.errors.agreement2 : false
              }
              className={style.label}
            >
              <div className={style.checkboxWraper}>
                <Checkbox
                  {...formik.getFieldProps("agreement2")}
                  checked={formik.values.agreement2}
                  id="agreement2"
                >
                  <Typography.Text>
                    <label htmlFor="agreement2" className={style.label}>
                      I acknowledge that I am submitting an order for the sale
                      of my shares. This is not an immediate sale at market
                      rate. This order will be executed by the Trading Desk at
                      BF GLOBAL IB MU as soon as possible at market rate.
                    </label>
                  </Typography.Text>
                </Checkbox>
              </div>
            </Form.Item>
            <Form.Item
              required
              hasFeedback
              help={
                formik.touched.agreement3 ? formik.errors.agreement3 : false
              }
              className={style.label}
            >
              <div className={style.checkboxWraper}>
                <Checkbox
                  {...formik.getFieldProps("agreement3")}
                  checked={formik.values.agreement3}
                  id="agreement3"
                >
                  <Typography.Text>
                    <label htmlFor="agreement3" className={style.label}>
                      I know that the fees mentioned in the Fee schedule will be
                      charged, and BF GLOBAL IB MU will charge $200 and any
                      fractional fees if I decide to sell 100% of the shares I
                      have.
                    </label>
                  </Typography.Text>
                </Checkbox>
              </div>
            </Form.Item>
          </>
        )}

        <Space direction="vertical" style={{ width: "100%" }}>
          {isSummaryDataExist && !(summaryData as SummaryData).isError && (
            <CustomBtn
              myType="submit"
              loading={formik.isSubmitting}
              disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
              className={style.button}
              onClick={formik.submitForm}
            >
              Confirm
            </CustomBtn>
          )}

          <CustomBtn
            myType="cancel"
            onClick={() => {
              setStep(0);
              window.scrollTo({
                top: 0,
                behavior: "smooth",
              });
            }}
            className={style.button}
          >
            Cancel
          </CustomBtn>
        </Space>
      </>
    );
  };

  return (
    <div className={style.wrapper}>
      <Row gutter={[0, 4]} justify="space-around" align="top">
        <Col
          span={24}
          className={style.header}
          style={{ textAlign: "center", margin: "40px auto" }}
        >
          <Typography.Title> Request to sell Public Shares</Typography.Title>
        </Col>
      </Row>

      <Row gutter={[0, 4]} justify="space-around" align="top">
        <Col sm={24} md={15}>
          <Form className={style.headerWrapperSection}>
            <Section>
              <div className={style.headerWrapper}>
                <CustomSteps
                  current={step}
                  labelPlacement="vertical"
                  items={[
                    {
                      title: "Request",
                    },
                    {
                      title: "Summary",
                    },
                  ]}
                />
              </div>
              <Divider />
              {step === 0 ? (
                <>
                  <Row>
                    <Col className={style.mainLabel}>
                      <Typography.Title level={5}>
                        Request to sell
                      </Typography.Title>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24}>
                      <Form.Item name="name" style={{ marginBottom: "5px" }}>
                        <Space
                          direction="vertical"
                          style={{ width: "100%", marginTop: "20px" }}
                        >
                          <Row>
                            <Col span={8}>
                              <label htmlFor="amount" className={style.label}>
                                Name
                              </label>
                            </Col>
                          </Row>
                          <div className={style.input}>
                            <Select
                              {...formik.getFieldProps("name")}
                              options={publicShareOptions}
                              onChange={(value) => {
                                formik.setFieldValue("name", value);
                                setSelectedShareToSell(value);
                              }}
                            />
                          </div>
                        </Space>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="amount"
                        style={{ marginBottom: "5px" }}
                        help={
                          formik.touched.amount
                            ? ReactHtmlParser(formik.errors.amount ?? "")
                            : false
                        }
                      >
                        <Space
                          direction="vertical"
                          style={{ width: "100%", marginTop: "20px" }}
                        >
                          <Row>
                            <Col span={8}>
                              <label htmlFor="amount" className={style.label}>
                                Enter amount of shares
                              </label>
                            </Col>
                            <Col span={16} style={{ textAlign: "right" }}>
                              <p style={{ margin: 0 }}>
                                <b>Available balance:</b>{" "}
                                {shareDetails?.balance ?? 0}{" "}
                                {shareDetails?.currency.toLocaleUpperCase()}
                              </p>
                            </Col>
                          </Row>
                          <div className={style.input}>
                            <Input
                              {...formik.getFieldProps("amount")}
                              type="tel"
                              onInput={handleAmountInputChange}
                              placeholder={`min. 0.001 - max. ${shareDetails?.balance}`}
                            />
                          </div>
                        </Space>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row justify="space-between">
                    <Col span={24}>
                      <PercentageBtns
                        handleAmountPercentage={handleAmountPercentage}
                        selectedTag={selectedTag}
                      />
                    </Col>
                  </Row>

                  <Space
                    direction="vertical"
                    style={{ marginTop: "40px", width: "100%" }}
                  >
                    <CustomBtn
                      myType="submit"
                      onClick={() => {
                        window.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                        getEstimations();
                      }}
                      disabled={!formik.dirty || formik.isSubmitting}
                      className={style.button}
                    >
                      Continue
                    </CustomBtn>

                    <CustomBtn
                      myType="cancel"
                      onClick={() =>
                        history.push(
                          match.url.replace(
                            "/request-to-sell",
                            "/public-equity"
                          )
                        )
                      }
                      className={style.button}
                    >
                      Cancel
                    </CustomBtn>
                  </Space>
                </>
              ) : (
                <div style={{ marginTop: "50px" }}>{renderSummary()}</div>
              )}
            </Section>
          </Form>
        </Col>
        <Col sm={24} md={7}>
          <Faq faq={requestToSellFaq} />
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (store: TReducers) => ({
  investorId: store.userInfo.investor_id,
  companyInvestorId: store.companyReducer.companyDetails.investor_id,
  userPublicShares: store.fundsReducer.pesUserWallets,
});

const mapDispatchToProps = (dispatch: Function) => ({
  setFundsData: (investorId: number, refetch: boolean) =>
    dispatch(setFundsData(investorId, refetch)),
  setAccessingFundsAgreementCreatedAt: () =>
    dispatch(setAccessingFundsAgreementCreatedAt()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RequestToSell)
);
